import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  type PortfolioMatrixJob,
  portfolioMatrixQueryKey,
} from "@/hooks/queries/portfolio/useGetPortfolioMatrix";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { guacHttpClient } from "@/lib/http";
import {
  CreateJobUpdateRequest,
  DashboardData,
  HttpResponse,
  JobBottleneck,
  JobDifficulty,
  JobUpdate,
} from "@/types";

// JobUpdate enums → the lowercase shape the portfolio matrix uses.
const toMatrixDifficulty = (difficulty: JobDifficulty | null) =>
  difficulty
    ? (difficulty
        .toLowerCase()
        .replace("_", "-") as PortfolioMatrixJob["difficulty"])
    : null;

const toMatrixBottleneck = (bottleneck: JobBottleneck | null) =>
  bottleneck
    ? (bottleneck.toLowerCase() as PortfolioMatrixJob["bottleneck"])
    : null;

export function useCreateJobUpdate() {
  const queryClient = useQueryClient();
  const currentUser = useGetCurrentUser();

  const mutation = useMutation({
    mutationFn: async ({
      companyId,
      createJobUpdateRequest,
      jobId,
    }: {
      companyId: string;
      createJobUpdateRequest: CreateJobUpdateRequest;
      jobId: string;
    }) => {
      const path = `companies/${companyId}/jobs/${jobId}/job-updates`;
      const res = await guacHttpClient.post<
        JobUpdate,
        HttpResponse<JobUpdate>,
        CreateJobUpdateRequest
      >(path, createJobUpdateRequest);
      return res.data;
    },
    onSuccess: (
      createdJobUpdate,
      { companyId, jobId, createJobUpdateRequest },
    ) => {
      const { difficulty, bottleneck, description } = createJobUpdateRequest;
      if (
        difficulty === undefined &&
        bottleneck === undefined &&
        description === undefined
      )
        return;

      // Patch the matrix row in place — the matrix is an expensive call, so we
      // avoid invalidating/refetching.
      queryClient.setQueryData<PortfolioMatrixJob[]>(
        portfolioMatrixQueryKey,
        (jobs) =>
          jobs?.map((job) =>
            job.id === jobId
              ? {
                  ...job,
                  ...(difficulty !== undefined && {
                    difficulty: toMatrixDifficulty(difficulty),
                  }),
                  ...(bottleneck !== undefined && {
                    bottleneck: toMatrixBottleneck(bottleneck),
                  }),
                  ...(description !== undefined && {
                    pulseDescription: description,
                  }),
                }
              : job,
          ),
      );

      // Set cache in dashboard. A written description also becomes the
      // author's latest per-staffer pulse, newest-first.
      const authorId = currentUser?.id;
      const upsertPulse = (pulses: DashboardData["jobs"][number]["pulses"]) => {
        if (!description || authorId === undefined) return pulses;
        return [
          {
            userId: authorId,
            description,
            createdAt: createdJobUpdate.createdAt,
          },
          ...pulses.filter((pulse) => pulse.userId !== authorId),
        ];
      };
      queryClient.setQueriesData<DashboardData>(
        { queryKey: ["dashboard"] },
        (dashboardData) =>
          dashboardData
            ? {
                ...dashboardData,
                jobs: dashboardData.jobs.map((job) =>
                  job.id === jobId
                    ? {
                        ...job,
                        ...(difficulty !== undefined && { difficulty }),
                        ...(bottleneck !== undefined && { bottleneck }),
                        ...(description !== undefined && {
                          pulseDescription: description,
                        }),
                        pulses: upsertPulse(job.pulses),
                      }
                    : job,
                ),
              }
            : dashboardData,
      );

      if (description !== undefined) {
        queryClient.setQueriesData<JobUpdate[]>(
          { queryKey: ["job-updates", companyId, jobId] },
          (jobUpdates) =>
            jobUpdates ? [createdJobUpdate, ...jobUpdates] : [createdJobUpdate],
        );
      }
    },
  });

  return mutation;
}
